import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TenantLogo } from '../../features/tenant/components/TenantLogo';
import { useI18n } from '../../i18n/useI18n';
import { resolveLacTenantPath } from '../../routes/lacPaths';
import type { LacTenantUsersGroup } from '../../types/lac';
import { LacUserRow } from './LacUserRow';
import {
  resolveUserKnowledgeBaseCount,
  userListKey,
} from './lacUsersTabFilters';

type LacUsersTenantGroupProps = {
  group: LacTenantUsersGroup;
  isExpanded: boolean;
  userCount: number | null | undefined;
  maxUsers: number | null | undefined;
  metricsByUserKey: Map<string, number>;
  onToggle: (tenantName: string) => void;
};

export function LacUsersTenantGroup({
  group,
  isExpanded,
  userCount,
  maxUsers,
  metricsByUserKey,
  onToggle,
}: LacUsersTenantGroupProps) {
  const { translate } = useI18n();
  const panelId = `lac-users-panel-${group.tenantName}`;
  const titleId = `lac-users-tenant-${group.tenantName}`;
  const isTenantFull = maxUsers != null && (userCount ?? 0) >= maxUsers;
  const openOrganisationLabel = translate('lac.users.openOrganisation', {
    name: group.displayName,
  });

  return (
    <li className="lac-users__group">
      <div className="lac-users__group-header">
        <button
          type="button"
          className="lac-users__group-toggle"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => onToggle(group.tenantName)}
        >
          <ChevronDown
            className={[
              'lac-users__group-chevron',
              isExpanded && 'lac-users__group-chevron--expanded',
            ]
              .filter(Boolean)
              .join(' ')}
            strokeWidth={2}
            aria-hidden
          />
          <TenantLogo
            className="lac-users__group-logo"
            logo={group.logo}
            label={group.displayName}
          />
          <span id={titleId} className="lac-users__group-title">
            {group.displayName}
          </span>
          <span
            className={[
              'lac-users__group-meta',
              isTenantFull && 'lac-users__group-meta--danger',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {maxUsers != null
              ? translate('lac.users.userCountWithMax', {
                  count: String(userCount ?? 0),
                  max: String(maxUsers),
                })
              : translate('lac.users.userCount', {
                  count: String(userCount ?? 0),
                })}
          </span>
        </button>
        <Link
          to={resolveLacTenantPath(group.tenantName)}
          className="lac-users__group-open"
          aria-label={openOrganisationLabel}
        >
          <ArrowRight
            className="lac-users__group-open-icon"
            strokeWidth={2}
            aria-hidden
          />
        </Link>
      </div>

      {isExpanded && (
        <ul id={panelId} className="lac-users__users" aria-labelledby={titleId}>
          {group.users.length === 0 ? (
            <li className="lac-users__empty-row">
              {translate('lac.users.tenantEmpty')}
            </li>
          ) : (
            group.users.map((user) => (
              <LacUserRow
                key={userListKey(user)}
                user={user}
                tenantName={group.tenantName}
                knowledgeBaseCount={resolveUserKnowledgeBaseCount(
                  user,
                  metricsByUserKey,
                )}
              />
            ))
          )}
        </ul>
      )}
    </li>
  );
}
